/** API error with parsed response body for validation messages */
import {
  clearAuthTokens,
  getApiBaseUrl,
  getLoginRedirectPath,
  refreshAccessToken,
  redirectToLogin,
} from './authTokens';
import { MAMBA_AUTH_CUTOVER } from './authMode';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data: Record<string, unknown> = {}
  ) {
    super(message);
    this.name = 'ApiError';
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/** Human-readable message for an error payload: detail/error first, then the first field error. */
export function errorMessageFromData(data: Record<string, unknown>, status: number): string {
  const detailData = typeof data.detail === 'object' && data.detail !== null
    ? (data.detail as Record<string, unknown>)
    : null;
  if (detailData && typeof detailData.detail === 'string' && detailData.detail) return detailData.detail;
  if (typeof data.detail === 'string' && data.detail) return data.detail;
  if (typeof data.error === 'string' && data.error) return data.error;
  // DRF field validation errors look like { field: ["message"] }. Surface the first one so a
  // rejected form explains itself instead of "Request failed with status 400".
  for (const [field, value] of Object.entries(data)) {
    const first = Array.isArray(value) ? value[0] : undefined;
    if (typeof first !== 'string' || !first) continue;
    if (field === 'non_field_errors' || field === 'email') return first;
    const label = field.replace(/_/g, ' ');
    return `${label.charAt(0).toUpperCase()}${label.slice(1)}: ${first}`;
  }
  return `Request failed with status ${status}`;
}

class ApiClient {
  private baseURL = getApiBaseUrl();

  private failAuth(): never {
    redirectToLogin();
    throw new Error('Authentication expired. Please log in again.');
  }

  private async fetchWithAuthRetry(doFetch: () => Promise<Response>): Promise<Response> {
    let response = await doFetch();
    if (response.status !== 401) {
      return response;
    }

    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      this.failAuth();
    }

    response = await doFetch();
    if (response.status === 401) {
      this.failAuth();
    }
    return response;
  }

  private async handleResponse(response: Response) {
    if (!response.ok) {
      const data = await response.json().catch(() => ({})) as Record<string, unknown>;
      const detailData = typeof data.detail === 'object' && data.detail !== null
        ? (data.detail as Record<string, unknown>)
        : null;
      const errorCode = (data.error_code as string) || (detailData?.error_code as string) || '';
      const billingPath = (data.billing_path as string) || (detailData?.billing_path as string) || '/tenant?tab=billing&locked=1';
      if (response.status === 403 && errorCode === 'TENANT_BILLING_LOCKED') {
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
        if (typeof window !== 'undefined' && currentPath !== '/tenant') {
          window.location.href = billingPath;
        }
      }
      throw new ApiError(errorMessageFromData(data, response.status), response.status, data);
    }

    return response.json();
  }

  /** Public endpoints: 401 throws without redirect (e.g. invalid login credentials). */
  private async handlePublicResponse(response: Response) {
    if (response.status === 401) {
      const data = await response.json().catch(() => ({})) as Record<string, unknown>;
      const message = (data.detail as string) || (data.error as string) || 'Authentication failed.';
      throw new ApiError(message, 401, data);
    }
    return this.handleResponse(response);
  }

  /**
   * The Authorization header, or nothing after the cutover.
   *
   * In BFF mode the credential is the session cookie, which every request here
   * already sends with `credentials: 'include'`. Attaching a bearer as well
   * would mean reading a token out of `localStorage` — the thing the cutover
   * removes — and the backend would ignore it anyway.
   */
  private bearer(): Record<string, string> {
    if (MAMBA_AUTH_CUTOVER) {
      return {};
    }
    const token = localStorage.getItem('accessToken');
    return { Authorization: `Bearer ${token}` };
  }

  private getAuthHeaders() {
    return {
      ...this.bearer(),
      'Content-Type': 'application/json',
    };
  }

  async get(
    url: string,
    params?: Record<string, string | number | boolean | null | undefined>
  ) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        queryParams.append(key, String(value));
      });
    }

    const query = queryParams.toString();
    const requestUrl = query ? `${this.baseURL}${url}?${query}` : `${this.baseURL}${url}`;

    const response = await this.fetchWithAuthRetry(() =>
      fetch(requestUrl, {
        headers: this.getAuthHeaders(),
        credentials: 'include',
      })
    );

    return this.handleResponse(response);
  }

  async post(url: string, data?: any) {
    const response = await this.fetchWithAuthRetry(() =>
      fetch(`${this.baseURL}${url}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: data ? JSON.stringify(data) : undefined,
        credentials: 'include',
      })
    );

    return this.handleResponse(response);
  }

  /** GET binary (e.g. authenticated capture image). Caller should revoke blob URLs when done. */
  async fetchAuthorizedBlob(path: string): Promise<Blob> {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const response = await this.fetchWithAuthRetry(() => {
      return fetch(`${this.baseURL}${normalizedPath}`, {
        method: 'GET',
        headers: { ...this.bearer() },
        credentials: 'include',
      });
    });

    if (!response.ok) {
      throw new Error(`Failed to load resource (${response.status})`);
    }
    return response.blob();
  }

  /** multipart/form-data (no Content-Type; browser sets boundary) */
  async postFormData(url: string, formData: FormData) {
    const response = await this.fetchWithAuthRetry(() => {
      return fetch(`${this.baseURL}${url}`, {
        method: 'POST',
        // No Content-Type: the browser sets the multipart boundary.
        headers: { ...this.bearer() },
        body: formData,
        credentials: 'include',
      });
    });
    return this.handleResponse(response);
  }

  async put(url: string, data?: any) {
    const response = await this.fetchWithAuthRetry(() =>
      fetch(`${this.baseURL}${url}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: data ? JSON.stringify(data) : undefined,
        credentials: 'include',
      })
    );

    return this.handleResponse(response);
  }

  async patch(url: string, data?: any) {
    const response = await this.fetchWithAuthRetry(() =>
      fetch(`${this.baseURL}${url}`, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: data ? JSON.stringify(data) : undefined,
        credentials: 'include',
      })
    );

    return this.handleResponse(response);
  }

  async delete(url: string) {
    const response = await this.fetchWithAuthRetry(() =>
      fetch(`${this.baseURL}${url}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
        credentials: 'include',
      })
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({})) as Record<string, unknown>;
      const detailData = typeof data.detail === 'object' && data.detail !== null
        ? (data.detail as Record<string, unknown>)
        : null;
      const errorCode = (data.error_code as string) || (detailData?.error_code as string) || '';
      const billingPath = (data.billing_path as string) || (detailData?.billing_path as string) || '/tenant?tab=billing&locked=1';
      if (response.status === 403 && errorCode === 'TENANT_BILLING_LOCKED') {
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
        if (typeof window !== 'undefined' && currentPath !== '/tenant') {
          window.location.href = billingPath;
        }
      }
      const nestedDetailMessage = detailData && typeof detailData.detail === 'string' ? detailData.detail : undefined;
      const message = nestedDetailMessage || (data.detail as string) || `Request failed with status ${response.status}`;
      throw new ApiError(message, response.status, data);
    }
  }

  /**
   * GET that reports a 401 instead of redirecting. "Am I signed in?" runs on every
   * page load, so redirecting here would send anonymous visitors on public pages,
   * such as /register, straight to the login page.
   */
  async probe(url: string) {
    const response = await fetch(`${this.baseURL}${url}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
      credentials: 'include',
    });
    return this.handlePublicResponse(response);
  }

  // Public methods for unauthenticated requests
  async publicPost(url: string, data?: any) {
    try {
      const response = await fetch(`${this.baseURL}${url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: data ? JSON.stringify(data) : undefined,
        credentials: 'include',
      });

      return this.handlePublicResponse(response);
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        const apiUrl = `${this.baseURL}${url}`;
        throw new Error(
          `Network error: Unable to connect to ${apiUrl}. ` +
          `Please ensure the backend server is running and CORS is configured correctly. ` +
          `Current API URL: ${this.baseURL}`
        );
      }
      throw error;
    }
  }
}

export const apiClient = new ApiClient();

// Re-export for callers that need redirect path without importing authTokens directly
export { getLoginRedirectPath, clearAuthTokens };
